import type { QueryClient, InfiniteData } from '@tanstack/react-query';
import type { HistoryType } from 'src/@types/historyTables';
import { createQueryKey } from 'src/utils/query/createQueryKey';

interface PageData<T> {
	records: T[];
	nextPage: string | null;
}

const MAX_LONG_TERM_CACHE_SIZE = 5000;

type DedupableRecord = { txSig: string; txSigIndex: number } | { slot: number };

function isDuplicate<T extends DedupableRecord>(
	existing: T,
	newRecord: T
): boolean {
	if ('txSig' in newRecord && 'txSig' in existing) {
		return (
			existing.txSig === newRecord.txSig &&
			existing.txSigIndex === newRecord.txSigIndex
		);
	}
	if ('slot' in newRecord && 'slot' in existing) {
		return existing.slot === newRecord.slot;
	}
	return false;
}

/**
 * Optimistically insert a record into both short-term and long-term history query caches.
 * Uses partial key matching so it works regardless of stableParams / date filters.
 * Dedup by txSig+txSigIndex when available, falls back to slot.
 *
 * Records are inserted into the raw cache regardless of active UI filters.
 * Filtering is applied at the component layer (via filterFn), not here.
 */
export function optimisticallyInsertShortTermRecord<T extends DedupableRecord>(
	queryClient: QueryClient,
	historyType: HistoryType,
	userAccount: string,
	authorityStr: string,
	newRecord: T
) {
	// Short-term: InfiniteData<PageData<T>>
	queryClient.setQueriesData<InfiniteData<PageData<T>>>(
		{
			queryKey: createQueryKey(
				'short-term-history',
				historyType,
				userAccount,
				authorityStr
			),
		},
		(old) => {
			if (!old?.pages?.length) return old;

			const hasDuplicate = old.pages[0].records.some((existing) =>
				isDuplicate(existing, newRecord)
			);
			if (hasDuplicate) return old;

			return {
				...old,
				pages: [
					{
						...old.pages[0],
						records: [newRecord, ...old.pages[0].records],
					},
					...old.pages.slice(1),
				],
			};
		}
	);

	// Long-term: T[]
	queryClient.setQueriesData<T[]>(
		{
			queryKey: createQueryKey(
				'long-term-history',
				historyType,
				userAccount,
				authorityStr
			),
		},
		(old) => {
			if (!old) return old;

			const hasDuplicate = old.some((existing) =>
				isDuplicate(existing, newRecord)
			);
			if (hasDuplicate) return old;

			const updated = [newRecord, ...old];
			return updated.length > MAX_LONG_TERM_CACHE_SIZE
				? updated.slice(0, MAX_LONG_TERM_CACHE_SIZE)
				: updated;
		}
	);
}
